<script setup lang="ts">
import { useElementSize } from '@vueuse/core';

const { t } = useTexts();

// Detecta bots para melhorar performance em testes
const { isBot } = usePerformanceBotBypass();

const layoutStore = useLayoutStore();
const { showMoneyFormat } = useWallet();
const { parameters } = useParameters();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { isGameModalOpen } = storeToRefs(useCasinoStore());
const { userCanOnlyWithdraw } = useUserLimits();
const { openRegister } = useAuth();

const refTopBar = ref<HTMLDivElement | null>(null);

const { height: topBarHeight } = useElementSize(
  refTopBar,
  { height: 0, width: 0 },
  { box: 'border-box' },
);

const expires_days = new Date();

expires_days.setDate(new Date().getDate() + 365);

const isTopBarClosed = useCookie('topbar_closed', {
  default: () => false,
  path: '/',
  expires: expires_days,
});

const referralBonusAmount = computed<number>(() => {
  return parameters.value?.referral_bonus_amount || 0;
});

const formatedBonusAmount = computed(() => {
  return referralBonusAmount.value
    ? showMoneyFormat({ value: referralBonusAmount.value / 100 })
    : '-';
});

const activeReferralBonus = computed<boolean>(() => {
  return !!parameters.value?.referral_bonus_active || false;
});

const bonusType = computed<string>(() => {
  return parameters.value?.referral_bonus_type || 'money_bonus';
});

const formatedBonusType = computed(() => {
  return bonusType.value === 'money_real'
    ? t('account.refer.real').toLocaleUpperCase()
    : t('account.refer.bonus').toLocaleLowerCase();
});

const isTopBarVisible = computed(() => {
  // Oculta TopBar para bots (melhora performance em testes)
  if (isBot.value) {
    return false;
  }

  if (!layoutStore.topbar.isVisible || isGameModalOpen.value) {
    return false;
  }

  if (userCanOnlyWithdraw.value) {
    return false;
  }

  return referralBonusAmount.value && activeReferralBonus.value;
});

const onTopBarClick = () => {
  if (!isAuthenticated.value) {
    openRegister();

    return;
  }

  navigateTo(localeLink('/user/refers'));
};

function closeTopBar() {
  layoutStore.closeTopBar();
  isTopBarClosed.value = true;
}

onBeforeMount(() => {
  if (!isTopBarClosed.value) {
    layoutStore.openTopBar();
  }
});

watch(
  isTopBarClosed,
  (isClosed: boolean) => {
    if (!isClosed) {
      layoutStore.openTopBar();
    }
  },
  { immediate: true },
);

watch(
  referralBonusAmount,
  (newAmountValue) => {
    if (!newAmountValue) {
      return layoutStore.setTopBarHeight(0);
    }

    if (!isTopBarClosed.value) {
      return layoutStore.openTopBar();
    }
  },
  {
    immediate: true,
  },
);

watch(topBarHeight, (newHeight) => {
  if (isTopBarVisible.value) {
    layoutStore.setTopBarHeight(newHeight);
  } else {
    layoutStore.setTopBarHeight(0);
  }
});
</script>

<template>
  <div
    v-if="isTopBarVisible"
    id="divTopBar"
    ref="refTopBar"
    :class="$style.topbar"
  >
    <span @click.prevent="onTopBarClick">
      {{
        $t('components.top_bar.refer_friend', {
          amount: formatedBonusAmount,
          type: formatedBonusType,
        }) + '💥'
      }}
    </span>
    <button @click.prevent="onTopBarClick">
      {{ $t('components.top_bar.refer_action') }}
    </button>
    <div
      :class="$style.closeButton"
      @click.prevent="closeTopBar()"
    >
      <NuxtIcon name="close" />
    </div>
  </div>
</template>

<style module lang="postcss">
.topbar {
  @apply w-full bg-sidebar-cta-bg text-sidebar-cta-text text-center flex items-center justify-center gap-3 md:gap-2 py-1 md:py-1.5 pl-2 pr-8 md:px-2 leading-4;

  span {
    @apply cursor-pointer font-medium text-2xs md:text-sm line-clamp-2;
    @media (max-width: 395px) {
      @apply text-[.68rem] leading-3;
    }
    @media (max-width: 380px) {
      @apply text-[.62rem];
    }
    @media (max-width: 360px) {
      @apply text-[.58rem];
    }
    @media (max-width: 330px) {
      @apply text-[.52rem];
    }
    /* @apply max-w-[80%] overflow-hidden whitespace-nowrap; */
  }

  button {
    @apply bg-sidebar-cta-text text-sidebar-cta-bg px-2 py-1 rounded text-xs md:text-sm font-normal md:font-medium;
  }

  .closeButton {
    @apply absolute right-0 md:right-2 p-2 opacity-60 hover:opacity-100 cursor-pointer;

    svg {
      @apply fill-texts text-sm md:text-lg;
    }
  }

  @media print {
    display: none !important;
  }
}
</style>
